/* eslint-disable react/jsx-key */
/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  getApiTargetInfo,
  hasAccesPermits,
  isValidYearRange,
  UtilityUnitSelection,
} from 'core'
import { connect } from 'react-redux'
import {
  getUtilityNameMap,
  getUnitNameMap,
  getUnits,
  getPermitActions,
  getEnvConfig,
  getValidYearRange,
} from 'core/selectors'
import CommonScreen from '../component/CommonScreen'
import { Button, InputNumber, Input, DatePicker, message } from 'antd'
import * as Actions from 'core/EventActions'
import styled from 'styled-components'
import { CriteriaRow } from 'core/screen-util'
import getTPSScreenInfo from './tps-screens'
import moment from 'moment'
import { ErrorContext, PNodeZoneContext } from 'context'
import JcTmpErrorDialog from 'core/component/JcTmpErrorDialog'

const Label = styled.div`
  display: grid;
  justify-content: end;
  align-content: center;
`
const getLabel = label => <Label>{label} :</Label>
const NumberComponent = ({
  style = {},
  name,
  defaultValue = 0,
  displayLabel,
  insertData,
  setInsertData,
}) => (
  <>
    {getLabel(displayLabel)}
    <InputNumber
      style={{ width: '100%', ...style }}
      value={insertData[name]}
      defaultValue={defaultValue}
      onChange={value => setInsertData({ ...insertData, [name]: value })}
    />
  </>
)
const InputComponent = ({
  style = {},
  name,
  displayLabel,
  insertData,
  setInsertData,
}) => (
  <>
    {getLabel(displayLabel)}
    <Input
      style={{ width: '100%', ...style }}
      value={insertData[name]}
      onChange={e => setInsertData({ ...insertData, [name]: e.target.value })}
    />
  </>
)

const TPSMappingScreen = ({
  location,
  apiTargetInfo,
  actionPermits,
  envConfig,
  units,
  utilityNameMap,
  unitNameMap,
  validYearRange,
}) => {
  const [insertEvent, setInsertEvent] = useState(false)
  const [errorData, setErrorData] = useState([])
  const [importing, setImporting] = useState(false)
  const {
    title,
    headers = [],
    useDefaultUnit = true,
    allowCriteria = false,
    initLoadApi,
    commonScreenProps = {},
    insertCompProps = {},
    saveTask,
    retrieveTask,
    importTask,
    deleteTask,
  } = getTPSScreenInfo(location.pathname.substring(1))
  const [selectedDateRangePicker, setSelectedDateRagePicker] = useState([
    moment()
      .subtract(1, 'month')
      .startOf('month'),
    moment(),
  ])
  const [initData, setInitData] = useState([])
  const { emktIdUnitNameMap, pnodeIdUnitNameMap } = useContext(PNodeZoneContext)
  const { setError } = useContext(ErrorContext)

  // const dataWriteAccess = useMemo(
  //   _ =>
  //     hasAccesPermits({
  //       authGodMode: envConfig.authGodMode,
  //       actionPermits,
  //       eventActions: [
  //         Actions.TPS_DATA_WRITE
  //       ],
  //       matchAny: true,
  //     }),
  //   [actionPermits, envConfig.authGodMode]
  // )

  const mappingWriteAccess = useMemo(
    _ =>
      hasAccesPermits({
        authGodMode: envConfig.authGodMode,
        actionPermits,
        eventActions: [Actions.TPS_MAPPING_WRITE],
        matchAny: true,
      }),
    [actionPermits, envConfig.authGodMode]
  )

  const isEditableGrid = ({ header }) => {
    if (
      header.ident === 'modifier' ||
      header.ident === 'modifiedDate' ||
      header.isKey
    )
      return false

    return mappingWriteAccess
  }

  useEffect(() => {
    if (initLoadApi)
      initLoadApi(apiTargetInfo)().fork(console.log, d => setInitData(d))
  }, [apiTargetInfo, initLoadApi])

  const headerWithFormatter = headers.map(header =>
    header.ident === 'utility'
      ? {
          ...header,
          dataFormatter: ({ value }) =>
            value + (utilityNameMap[value] ? '-' : '') + utilityNameMap[value],
          sortIndexGetter: ({ rowData }) =>
            utilityNameMap[rowData.utility] || rowData.utility,
          alignment: 'left',
        }
      : header.ident === 'unitId'
      ? {
          ...header,
          dataFormatter: ({ value, rowData }) => {
            if (useDefaultUnit) {
              return (
                value.substring(3) +
                (unitNameMap[value] ? '-' : '') +
                unitNameMap[value]
              )
            } else {
              const { pnodeId, emktId } = rowData
              return emktId
                ? emktIdUnitNameMap[emktId] || emktId
                : pnodeId
                ? pnodeIdUnitNameMap[pnodeId] || pnodeId
                : value
            }
          },
          sortIndexGetter: ({ value, rowData }) => {
            if (useDefaultUnit) {
              return (
                value.substring(3) +
                (unitNameMap[value] ? '-' : '') +
                unitNameMap[value]
              )
            } else {
              const { pnodeId, emktId } = rowData
              return emktId
                ? emktIdUnitNameMap[emktId] || emktId
                : pnodeId
                ? pnodeIdUnitNameMap[pnodeId] || pnodeId
                : value
            }
          },
          alignment: 'left',
        }
      : header
  )

  const getComponent = ({ header, ...compProps }) => {
    const { ident, type, display } = header
    if (type === 'str') {
      return (
        <InputComponent name={ident} displayLabel={display} {...compProps} />
      )
    } else if (type === 'num') {
      return (
        <NumberComponent name={ident} displayLabel={display} {...compProps} />
      )
    } else {
      return null
    }
  }

  const insertComp = ({ insertData, setInsertData, onAdd, onCancel }) => {
    const {
      gridTemplateColumns = '80px 150px 200px 150px 100px 120px 120px 180px 100px 100px',
    } = insertCompProps
    return (
      <CriteriaRow style={{ padding: '5px', paddingLeft: '10px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns,
            gridGap: '5px',
          }}
        >
          <Label>Unit :</Label>
          <div style={{ gridColumn: 'span 3' }}>
            <UtilityUnitSelection
              utility={insertData.utility}
              unit={insertData.unit}
              onUnitChange={value =>
                setInsertData({
                  ...insertData,
                  utility: value[0],
                  unit: value[1].substring(3),
                })
              }
            />
          </div>
          {headers
            .filter(
              h =>
                !['utility', 'unit', 'modifier', 'modifiedDate'].includes(
                  h.ident
                )
            )
            .map(header => getComponent({ header, insertData, setInsertData }))}
          {/* <div style={{ gridColumn: 'span 7' }} /> */}
          <div
            style={{
              gridColumn: 'span 2',
              gridRowGap: '10px',
              width: '100%',
              display: 'grid',
              gridTemplateColumns: 'auto auto auto',
              paddingLeft: '5px',
              gridColumnGap: '10px',
            }}
          >
            <Button type="primary" onClick={() => onAdd(true)}>
              Add
            </Button>
            <Button type="primary" onClick={() => onAdd(false)}>
              Add & Hide
            </Button>
            <Button type="danger" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </CriteriaRow>
    )
  }

  const withCriteria = (state, props, selectedUnits) => {
    return allowCriteria
      ? {
          start: selectedDateRangePicker[0],
          end: selectedDateRangePicker[1],
          units: (selectedUnits || []).filter(e => !!e).map(e => e.elementName), // TODO: generalise key element
        }
      : null
  }

  const beforeRetrieveValidation = ({ validYearRange }) => {
    return isValidYearRange(
      validYearRange,
      selectedDateRangePicker[0],
      selectedDateRangePicker[1]
    )
  }

  const screenCriteria = (s, p, u, onChange) => {
    return allowCriteria ? (
      <DatePicker.RangePicker
        style={{ marginLeft: '15px' }}
        allowClear={false}
        format="MM/DD/YYYY"
        value={selectedDateRangePicker}
        onChange={d => {
          setSelectedDateRagePicker(d)
          onChange()
        }}
      />
    ) : null
  }

  const extraCriteriaAfterButton = ({ selectedUnits }) => {
    return allowCriteria ? (
      <Button
        loading={importing}
        onClick={() => {
          if (!beforeRetrieveValidation({ validYearRange })) return

          setImporting(true)
          importTask(apiTargetInfo)({
            start: selectedDateRangePicker[0],
            end: selectedDateRangePicker[1],
            units: (selectedUnits || []).filter(e => !!e).map(e => e.unitId),
          }).fork(setError, jcTmpErrorList => {
            if (Array.isArray(jcTmpErrorList)) {
              const isError = (jcTmpErrorList || [])
                .map(f => f.severity)
                .filter(f => !!f)

              if (isError.length > 0) setErrorData(jcTmpErrorList)
            }
            message.info(
              'Data started importing, Please check data after sometime.'
            )
            setImporting(false)
          })
        }}
        style={{ marginLeft: '10px' }}
        type="danger"
        icon="thunderbolt"
      >
        Import Data
      </Button>
    ) : null
  }

  const getApplicableUnits = () =>
    units
      .filter(u => initData.some(e => u.unitId === e.utility + e.unit))
      .map(u => ({
        ...initData.find(e => u.unitId === e.utility + e.unit),
        ...u,
      }))

  const extraUnitSelectionProps = _ => ({
    limitedUnits: getApplicableUnits(),
    units: getApplicableUnits(),
    // itemToLabel: e => e.emktId,
  })

  return [
    <>
      {errorData && errorData.length > 0 && (
        <JcTmpErrorDialog
          visible={errorData.length > 0}
          errors={errorData}
          closeErrorPrompt={_ => setErrorData([])}
        />
      )}
      <CommonScreen
        title={title}
        header={headerWithFormatter}
        saveTask={saveTask}
        retrieveTask={retrieveTask}
        withCriteria={withCriteria}
        beforeRetrieveValidation={
          allowCriteria ? beforeRetrieveValidation : null
        }
        screenCriteria={screenCriteria}
        extraCriteriaAfterButton={extraCriteriaAfterButton}
        isEditableGrid={isEditableGrid}
        deleteTask={deleteTask}
        isAdminScreen={false}
        altBy={d => d.unitId}
        sortOptions={headerWithFormatter}
        insertComp={insertEvent && mappingWriteAccess && insertComp}
        insertCompHeight={90}
        onInsertEvent={b => setInsertEvent(mappingWriteAccess && b)}
        autoFixColByKey={false}
        headerRowHeight={60}
        extraUnitSelectionProps={extraUnitSelectionProps}
        {...commonScreenProps}
      />
    </>,
  ]
}

const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
  actionPermits: getPermitActions(state),
  envConfig: getEnvConfig(state),
  units: getUnits(state),
  utilityNameMap: getUtilityNameMap(state),
  unitNameMap: getUnitNameMap(state),
  validYearRange: getValidYearRange(state),
})

export default connect(mapStateToProps)(TPSMappingScreen)
